import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import httpRequest from '../utils/request';
import * as XLSX from 'xlsx';
// Endpoints relativos para aprovechar baseURL y fallback
const API_URL_SALES = '/sales';
const API_URL_PRODUCTS = '/product';

const Reports = () => {
  const navigate = useNavigate();

  // Fiscal status provisto por el slice `fiscal` (cargado por App.js en inicio)
  const fiscalStatus = useSelector((state) => state.fiscal && state.fiscal.fiscalStatus);
  // Determinar si el tenant es Responsable Monotributo. Backend usa condicionIVA===6 y/o regimenFiscal==='MONO'
  const isMonotributo = (() => {
    if (!fiscalStatus) return false;
    const cond = Number(fiscalStatus.condicionIVA || -1);
    const regimen = (fiscalStatus.regimenFiscal || '').toString().toUpperCase();
    const tipo = (fiscalStatus.tipoResponsable || '').toString().toLowerCase();
    return cond === 6 || regimen === 'MONO' || tipo.includes('monotr');
  })();

  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('summary'); // 'summary' or 'detailed'
  const [periodFilter, setPeriodFilter] = useState('all'); // all, today, week, month
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  // showSalesRange removed: ventas ahora se muestran en página separada
  const { user } = useSelector(state => state.auth || {});
  const { tenant } = useSelector(state => state.tenant || {});
  const tenantId = user?.tenantId?._id || user?.tenantId || tenant?._id || tenant?.id;
  const serviceType = user?.tenantId?.serviceType || 'full_pos';
  const isEcommerceLite = serviceType === 'ecommerce_lite';

  // Estados para valorización de stock
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showAllCategories, setShowAllCategories] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const now = new Date();
      let startDateStr = '';
      let endDateStr = '';

      if (periodFilter === 'today') {
        const d = new Date(now);
        d.setHours(0,0,0,0);
        startDateStr = d.toISOString();
      } else if (periodFilter === 'week') {
        const d = new Date(now);
        d.setDate(d.getDate() - 7);
        startDateStr = d.toISOString();
      } else if (periodFilter === 'month') {
        const d = new Date(now.getFullYear(), now.getMonth(), 1);
        startDateStr = d.toISOString();
      }

      // Si el usuario eligió fechas manuales, sobreescribir
      if (filterDateFrom) {
        const [y, m, d] = filterDateFrom.split('-');
        startDateStr = new Date(y, m - 1, d, 0, 0, 0).toISOString();
      }
      if (filterDateTo) {
        const [y, m, d] = filterDateTo.split('-');
        endDateStr = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
      }

      const [salesRes, productsRes, suppliersRes] = await Promise.all([
        httpRequest.get(`${API_URL_SALES}/all`, { 
          params: { 
            limit: 1000, 
            startDate: startDateStr || undefined, 
            endDate: endDateStr || undefined 
          } 
        }),
        httpRequest.get(`${API_URL_PRODUCTS}/all-products`),
        httpRequest.get('/suppliers', { params: { limit: 1000 } }).catch(() => ({ data: { items: [] } }))
      ]);

      const salesArray = Array.isArray(salesRes.data.items) 
        ? salesRes.data.items 
        : (Array.isArray(salesRes.data.sales) ? salesRes.data.sales : (Array.isArray(salesRes.data) ? salesRes.data : []));
      setSales(salesArray);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data?.items || suppliersRes.data || []);
    } catch (err) {
      console.error('Error al cargar datos:', err);
      toast.error('❌ Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  }, [periodFilter, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtrar ventas por período (Simplificado, ya que ahora filtramos en backend mayormente)
  const getFilteredSales = () => {
    if (!Array.isArray(sales)) return [];
    
    // Mantener un filtrado ligero para el modo "Todo" o cambios rápidos
    return sales.filter((sale) => {
      if (sale.paymentStatus === 'cancelled' || sale.status === 'cancelled') return false;
      return true;
    });
  };

  const filteredSales = getFilteredSales();

  // Calcular estadísticas
  const totalSales = filteredSales.length;
  const totalRevenue = filteredSales.reduce((acc, sale) => acc + (Number(sale.total) || 0), 0);
  const totalProfit = filteredSales.reduce((acc, sale) => {
    const saleCost = (sale.items || []).reduce((sum, item) => {
      let cost = item.costPrice;
      if (cost == null || cost === 0) {
        // Fallback to current product cost from state
        const pid = item.productId?._id || item.productId;
        const p = products.find(prod => String(prod._id) === String(pid));
        if (p) {
          if (p.variants && p.variants.length > 0 && item.variantId) {
            const v = p.variants.find(varnt => varnt.sku === item.variantId || (varnt._id && varnt._id.toString() === item.variantId));
            cost = v?.costPrice ?? p.costPrice;
          } else {
            cost = p.costPrice;
          }
        }
      }
      const itemTotalCost = ((Number(item.quantity) || 0) * (Number(cost) || 0));
      return sum + itemTotalCost;
    }, 0);
    const netRevenue = Number(sale.subtotal || sale.total || 0);
    const profitValue = netRevenue - saleCost;
    return acc + profitValue;
  }, 0);
  const averageSale = totalSales > 0 ? totalRevenue / totalSales : 0;

  // Productos más vendidos
  const productSalesMap = {};
  filteredSales.forEach((sale) => {
    (sale.items || []).forEach((item) => {
      // IMPORTANTE: item.productId puede venir poblado como objeto { _id, category }
      const productId = String(item.productId?._id || item.productId || 'unknown');
      
      // Buscar el nombre real del producto
      let productName = item.name;
      if (!productName) {
        const product = products.find(p => String(p._id) === productId);
        productName = product ? product.name : `Producto ${productId}`;
      }
      if (!productSalesMap[productId]) {
        productSalesMap[productId] = {
          name: productName,
          quantity: 0,
          revenue: 0,
          profit: 0,
        };
      }
      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      productSalesMap[productId].quantity += qty;
      productSalesMap[productId].revenue += qty * price;

      // Calcular ganancia por producto
      let cost = item.costPrice;
      if (cost == null || cost === 0) {
        const p = products.find(prod => String(prod._id) === productId);
        if (p) {
          if (p.variants && p.variants.length > 0 && item.variantId) {
            const v = p.variants.find(varnt => varnt.sku === item.variantId || (varnt._id && varnt._id.toString() === item.variantId));
            cost = v?.costPrice ?? p.costPrice;
          } else {
            cost = p.costPrice;
          }
        }
      }
      productSalesMap[productId].profit += qty * (price - (Number(cost) || 0));
    });
  });

  const topProducts = Object.entries(productSalesMap)
    .map(([id, data]) => ({ id, ...data }))
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 10);

  // Reporte de Reposición (Todos los productos vendidos en el período)
  const replenishmentData = (Object.entries(productSalesMap) || [])
    .map(([productId, data]) => {
      const product = products.find(p => String(p._id) === String(productId));
      const supplierId = product?.supplierId?._id || product?.supplierId;
      const sList = Array.isArray(suppliers) ? suppliers : [];
      const supplier = sList.find(s => String(s._id) === String(supplierId));
      return {
        id: productId,
        barcode: product?.barcode || 'N/A',
        name: data.name,
        costPrice: product?.costPrice || 0,
        supplierName: supplier?.nombre || product?.supplierId?.nombre || 'S/D',
        quantitySold: data.quantity,
        stock: product?.stock || 0
      };
    })
    .sort((a, b) => b.quantitySold - a.quantitySold);

  // Informe Detallado (Un renglón por cada item vendido con su fecha)
  const detailedReplenishmentLog = [];
  filteredSales.forEach(sale => {
    (sale.items || []).forEach(item => {
      const productId = String(item.productId?._id || item.productId || 'unknown');
      const product = products.find(p => String(p._id) === productId);
      const supplierId = product?.supplierId?._id || product?.supplierId;
      const sList = Array.isArray(suppliers) ? suppliers : [];
      const supplier = sList.find(s => String(s._id) === String(supplierId));
      
      detailedReplenishmentLog.push({
        date: sale.createdAt || sale.date,
        barcode: product?.barcode || item.barcode || 'N/A',
        name: item.name || product?.name || 'Producto',
        costPrice: Number(item.costPrice || product?.costPrice || 0),
        supplierName: supplier?.nombre || product?.supplierId?.nombre || 'S/D',
        quantity: Number(item.quantity) || 0,
        stock: product?.stock || 0
      });
    });
  });
  detailedReplenishmentLog.sort((a, b) => new Date(b.date) - new Date(a.date));

  const fmtTableDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Ventas por día (últimos 7 días)
  const last7Days = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    date.setHours(0, 0, 0, 0);

    const dayName = date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' });
    const daySales = sales.filter((sale) => {
      const saleDate = new Date(sale.createdAt || sale.date);
      return (
        saleDate.getDate() === date.getDate() &&
        saleDate.getMonth() === date.getMonth() &&
        saleDate.getFullYear() === date.getFullYear()
      );
    });

    const dayRevenue = daySales.reduce((acc, sale) => acc + sale.total, 0);
    last7Days.push({ date: dayName, count: daySales.length, revenue: dayRevenue });
  }

  const maxDayRevenue = Math.max(...last7Days.map(d => d.revenue), 1);

  // Productos con stock bajo
  const lowStockProducts = products.filter(p => p.stock > 0 && p.stock <= 5);

  // ===== VALORIZACIÓN DE STOCK =====
  // Obtener todas las categorías únicas
  const allCategories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();

  // Filtrar productos según categorías seleccionadas
  const filteredProducts = showAllCategories
    ? products
    : products.filter(p => selectedCategories.includes(p.category));

  // Calcular valorización de stock
  const stockValuation = filteredProducts.reduce((acc, product) => {
    const stock = Number(product.stock) || 0;
    const costPrice = Number(product.costPrice) || 0;
    const salePrice = Number(product.salePrice) || 0;

    // El stock del producto ya incluye la suma de variantes (ver productModel.js pre-save hook)
    // Solo necesitamos calcular el precio promedio ponderado si hay variantes con precios diferentes
    if (product.variants && product.variants.length > 0) {
      // Calcular valorización ponderada por variante
      let totalCost = 0;
      let totalSale = 0;

      product.variants.forEach(variant => {
        const variantStock = Number(variant.stock) || 0;
        const variantCost = Number(variant.costPrice) || costPrice;
        const variantPrice = Number(variant.price) || salePrice;

        totalCost += variantStock * variantCost;
        totalSale += variantStock * variantPrice;
      });

      acc.totalCostValue += totalCost;
      acc.totalSaleValue += totalSale;
      acc.totalUnits += stock; // Usar stock del producto (ya es la suma)
    } else {
      acc.totalCostValue += stock * costPrice;
      acc.totalSaleValue += stock * salePrice;
      acc.totalUnits += stock;
    }

    return acc;
  }, { totalCostValue: 0, totalSaleValue: 0, totalUnits: 0 });

  const potentialProfit = stockValuation.totalSaleValue - stockValuation.totalCostValue;

  // Calcular distribución de productos por categoría
  const categoryDistribution = allCategories.map(category => {
    const categoryProducts = products.filter(p => p.category === category);
    const count = categoryProducts.length;
    const percentage = products.length > 0 ? (count / products.length) * 100 : 0;

    // Valorización por categoría
    const categoryValuation = categoryProducts.reduce((acc, product) => {
      const stock = Number(product.stock) || 0;
      const costPrice = Number(product.costPrice) || 0;
      const salePrice = Number(product.salePrice) || 0;

      // El stock del producto ya incluye la suma de variantes
      if (product.variants && product.variants.length > 0) {
        let totalCost = 0;
        let totalSale = 0;

        product.variants.forEach(variant => {
          const variantStock = Number(variant.stock) || 0;
          const variantCost = Number(variant.costPrice) || costPrice;
          const variantPrice = Number(variant.price) || salePrice;

          totalCost += variantStock * variantCost;
          totalSale += variantStock * variantPrice;
        });

        acc.costValue += totalCost;
        acc.saleValue += totalSale;
        acc.units += stock; // Usar stock del producto (ya es la suma)
      } else {
        acc.costValue += stock * costPrice;
        acc.saleValue += stock * salePrice;
        acc.units += stock;
      }

      return acc;
    }, { costValue: 0, saleValue: 0, units: 0 });

    return {
      category,
      count,
      percentage,
      units: categoryValuation.units,
      costValue: categoryValuation.costValue,
      saleValue: categoryValuation.saleValue,
      potentialProfit: categoryValuation.saleValue - categoryValuation.costValue
    };
  }).sort((a, b) => b.percentage - a.percentage);

  // Manejadores de categorías
  const toggleCategory = (category) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
    setShowAllCategories(false);
  };

  const selectAllCategories = () => {
    setShowAllCategories(true);
    setSelectedCategories([]);
  };

  const deselectAllCategories = () => {
    setShowAllCategories(false);
    setSelectedCategories([]);
  };

  const downloadReplenishmentExcel = () => {
    try {
      const isDetailed = viewMode === 'detailed';
      const dataSet = isDetailed ? detailedReplenishmentLog : replenishmentData;

      if (dataSet.length === 0) {
        toast.warn('No hay datos para exportar en este período');
        return;
      }
      
      const dataToExport = dataSet.map(item => {
        const base = {
          'Desde': filterDateFrom || 'Inicio',
          'Hasta': filterDateTo || 'Hoy',
          'Código': item.barcode,
          'Descripción': item.name,
          'Precio Costo': item.costPrice,
          'Proveedor': item.supplierName,
          [isDetailed ? 'Cantidad' : 'Cant. Vendida']: isDetailed ? item.quantity : item.quantitySold,
          'Stock Actual': item.stock
        };
        if (isDetailed) {
          return { 'Fecha Venta': fmtTableDate(item.date), ...base };
        }
        return base;
      });

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, isDetailed ? "Ventas Detalladas" : "Resumen Reposición");
      
      const prefix = isDetailed ? 'ventas_detalladas' : 'reposicion_resumen';
      const fromStr = filterDateFrom ? filterDateFrom : 'inicio';
      const toStr = filterDateTo ? filterDateTo : 'hoy';
      const fileName = `${prefix}_del_${fromStr}_al_${toStr}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success('Descarga iniciada');
    } catch (e) {
      console.error('Error exportando reposición:', e);
      toast.error('Error al generar Excel');
    }
  };

  const downloadSalesExcel = async () => {
    try {
      toast.info('Preparando descarga de Excel...');
      const params = {};
      if (filterDateFrom) params.startDate = new Date(filterDateFrom).toISOString();
      if (filterDateTo) params.endDate = new Date(filterDateTo).toISOString();
      if (tenantId) params.tenantId = tenantId;
      const resp = await httpRequest.get('/reports/sales-export', { params, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const from = params.startDate ? params.startDate.slice(0, 10) : 'all';
      const to = params.endDate ? params.endDate.slice(0, 10) : 'all';
      a.href = url;
      a.download = `reportes_ventas_${from}_to_${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Descarga iniciada');
    } catch (e) {
      console.error('Error exportando ventas desde Reportes:', e?.response?.data || e.message || e);
      toast.error('No se pudo generar el Excel de ventas');
    }
  };

  return (
    <div style={{ padding: '30px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>📊 Reportes y Estadísticas</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button style={btnNav} onClick={() => navigate('/')}>🏠 Dashboard</button>
          {!isEcommerceLite && (
            <button style={{ ...btnNav, background: '#28a745' }} onClick={() => navigate('/sales')}>💵 Punto de Venta</button>
          )}
          <button style={{ ...btnNav, background: '#17a2b8' }} onClick={() => navigate('/sales-history')}>📋 Historial</button>
          {/* Ocultar botones de IVA si el tenant es Responsable Monotributo o es Ecommerce Lite */}
          {!isMonotributo && !isEcommerceLite && (
            <>
              <button style={{ ...btnNav, background: '#343a40' }} onClick={() => navigate('/reports/iva-sales')}>📑 IVA Ventas</button>
              <button style={{ ...btnNav, background: '#343a40' }} onClick={() => navigate('/reports/iva-purchases')}>📑 IVA Compras</button>
            </>
          )}
          <button style={{ ...btnNav, background: '#20c997' }} onClick={() => navigate('/reports/sales')}>Ventas</button>
          <button style={{ ...btnNav, background: '#dc3545' }} onClick={() => navigate('/login')}>🚪 Salir</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Cargando reportes...</p>
        </div>
      ) : (
        <>
          {/* Filtro de período */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <button
              style={periodFilter === 'all' ? btnFilterActive : btnFilter}
              onClick={() => setPeriodFilter('all')}
            >
              Todo
            </button>
            <button
              style={periodFilter === 'today' ? btnFilterActive : btnFilter}
              onClick={() => setPeriodFilter('today')}
            >
              Hoy
            </button>
            <button
              style={periodFilter === 'week' ? btnFilterActive : btnFilter}
              onClick={() => setPeriodFilter('week')}
            >
              Últimos 7 días
            </button>
            <button
              style={periodFilter === 'month' ? btnFilterActive : btnFilter}
              onClick={() => setPeriodFilter('month')}
            >
              Este mes
            </button>
          </div>

          {/* Filtro por rango de fechas para exportar/reportes detallados */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '700', display: 'block', marginBottom: '6px' }}>Fecha desde</label>
              <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} style={{ padding: '8px', borderRadius: 4, border: '1px solid #ccc' }} />
            </div>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '700', display: 'block', marginBottom: '6px' }}>Fecha hasta</label>
              <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} style={{ padding: '8px', borderRadius: 4, border: '1px solid #ccc' }} />
            </div>
            <div>
              <button
                style={{ ...btnFilter, background: '#6c757d', color: 'white', border: 'none' }}
                onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); }}
              >🧹 Limpiar fechas</button>
            </div>
            <div>
              <button
                style={{ ...btnFilterActive, background: '#20c997', borderColor: '#20c997' }}
                onClick={downloadSalesExcel}
              >📥 Exportar ventas</button>
            </div>
          </div>

          {/* Tarjetas de resumen */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            <div style={card}>
              <div style={cardIcon}>💰</div>
              <div style={cardTitle}>Total Vendido</div>
              <div style={cardValue}>${totalRevenue.toFixed(2)}</div>
            </div>
            <div style={{ ...card, backgroundColor: '#e6fffa', border: '1px solid #38b2ac' }}>
              <div style={cardIcon}>📈</div>
              <div style={cardTitle}>Utilidad (Ganancia)</div>
              <div style={{ ...cardValue, color: '#2c7a7b' }}>${totalProfit.toFixed(2)}</div>
            </div>
            <div style={card}>
              <div style={cardIcon}>🛒</div>
              <div style={cardTitle}>Ventas Realizadas</div>
              <div style={cardValue}>{totalSales}</div>
            </div>
            <div style={card}>
              <div style={cardIcon}>📊</div>
              <div style={cardTitle}>Ticket Promedio</div>
              <div style={cardValue}>${averageSale.toFixed(2)}</div>
            </div>
            <div style={card}>
              <div style={cardIcon}>⚠️</div>
              <div style={cardTitle}>Stock Bajo</div>
              <div style={cardValue}>{lowStockProducts.length}</div>
            </div>
          </div>

          {/* Gráfico de ventas últimos 7 días */}
          <div style={{ ...sectionBox, marginBottom: '30px' }}>
            <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>📈 Ventas últimos 7 días</h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', height: '200px' }}>
              {last7Days.map((day, idx) => (
                <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '100%',
                      backgroundColor: '#007bff',
                      height: `${(day.revenue / maxDayRevenue) * 180}px`,
                      minHeight: '5px',
                      borderRadius: '4px 4px 0 0',
                      position: 'relative',
                    }}
                    title={`${day.count} ventas - $${day.revenue.toFixed(2)}`}
                  >
                    <div style={{ position: 'absolute', top: '-20px', width: '100%', textAlign: 'center', fontSize: '11px', fontWeight: 'bold' }}>
                      ${day.revenue > 0 ? day.revenue.toFixed(0) : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', marginTop: '5px', textAlign: 'center' }}>{day.date}</div>
                  <div style={{ fontSize: '11px', color: '#666' }}>{day.count}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Productos más vendidos */}
          <div style={sectionBox}>
            <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>🏆 Top 10 Productos Más Vendidos</h2>
            {topProducts.length === 0 ? (
              <p style={{ color: '#666' }}>No hay datos de productos vendidos en este período.</p>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Producto</th>
                    <th style={thStyle}>Cantidad Vendida</th>
                    <th style={thStyle}>Ingresos</th>
                    <th style={thStyle}>Ganancia</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((product, idx) => (
                    <tr key={product.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={tdStyle}>{idx + 1}</td>
                      <td style={tdStyle}>{product.name}</td>
                      <td style={tdStyle}>{product.quantity}</td>
                      <td style={tdStyle}>${product.revenue.toFixed(2)}</td>
                      <td style={{ ...tdStyle, color: '#2c7a7b', fontWeight: 'bold' }}>${product.profit.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Reporte de Reposición */}
          <div style={{ ...sectionBox, marginTop: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <div>
                <h2 style={{ fontSize: '18px', margin: 0 }}>📦 Reposición y Log de Ventas</h2>
                <div style={{ marginTop: '10px', display: 'flex', gap: '5px' }}>
                  <button 
                    onClick={() => setViewMode('summary')}
                    style={viewMode === 'summary' ? btnMiniActive : btnMini}
                  >Resumen por producto</button>
                  <button 
                    onClick={() => setViewMode('detailed')}
                    style={viewMode === 'detailed' ? btnMiniActive : btnMini}
                  >Detallado por fecha</button>
                </div>
              </div>
              <button 
                onClick={downloadReplenishmentExcel}
                style={{ ...btnFilterActive, background: '#28a745', borderColor: '#28a745' }}
              >
                📥 Descargar Excel ({viewMode === 'summary' ? 'Resumen' : 'Detalle'})
              </button>
            </div>
            
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
              {viewMode === 'summary' 
                ? 'Agrupado por producto: total vendido de cada uno para reposición.'
                : 'Log detallado: cada producto vendido con su fecha y hora exacta.'}
            </p>

            {(viewMode === 'summary' ? replenishmentData : detailedReplenishmentLog).length === 0 ? (
              <p style={{ color: '#666' }}>No hay ventas registradas en este período.</p>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    {viewMode === 'detailed' && <th style={thStyle}>Fecha/Hora</th>}
                    <th style={thStyle}>Código</th>
                    <th style={thStyle}>Descripción</th>
                    <th style={thStyle}>P. Costo</th>
                    <th style={thStyle}>Proveedor</th>
                    <th style={thStyle}>{viewMode === 'detailed' ? 'Cantidad' : 'Cant. Vendida'}</th>
                    <th style={thStyle}>Stock Actual</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewMode === 'summary' ? replenishmentData : detailedReplenishmentLog).map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #dee2e6' }}>
                      {viewMode === 'detailed' && <td style={{ ...tdStyle, fontSize: '12px' }}>{fmtTableDate(item.date)}</td>}
                      <td style={tdStyle}>{item.barcode}</td>
                      <td style={tdStyle}>{item.name}</td>
                      <td style={tdStyle}>${item.costPrice.toFixed(2)}</td>
                      <td style={tdStyle}>{item.supplierName}</td>
                      <td style={{ ...tdStyle, fontWeight: 'bold', textAlign: 'center' }}>{viewMode === 'detailed' ? item.quantity : item.quantitySold}</td>
                      <td style={{ ...tdStyle, textAlign: 'center', color: item.stock <= 0 ? '#dc3545' : 'inherit' }}>
                        {item.stock}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ===== VALORIZACIÓN DE STOCK ===== */}
          <div style={{ ...sectionBox, marginTop: '30px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <h2 style={{ fontSize: '20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              💎 Valorización de Stock
            </h2>

            {/* Selector de Categorías */}
            <div style={{ marginBottom: '20px', background: 'rgba(255,255,255,0.1)', padding: '15px', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ fontSize: '16px', margin: 0 }}>📂 Filtrar por Categorías</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={selectAllCategories}
                    style={{
                      ...btnFilter,
                      background: showAllCategories ? '#28a745' : 'rgba(255,255,255,0.2)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)',
                      fontSize: '12px',
                      padding: '6px 12px'
                    }}
                  >
                    ✓ Todas
                  </button>
                  <button
                    onClick={deselectAllCategories}
                    style={{
                      ...btnFilter,
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)',
                      fontSize: '12px',
                      padding: '6px 12px'
                    }}
                  >
                    ✗ Ninguna
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {allCategories.map(category => (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      background: (showAllCategories || selectedCategories.includes(category))
                        ? 'rgba(255,255,255,0.9)'
                        : 'rgba(255,255,255,0.1)',
                      color: (showAllCategories || selectedCategories.includes(category))
                        ? '#667eea'
                        : 'white',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {category} ({categoryDistribution.find(c => c.category === category)?.count || 0})
                  </button>
                ))}
              </div>
            </div>

            {/* Tarjetas de Resumen de Valorización */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '20px', borderRadius: '8px', backdropFilter: 'blur(10px)' }}>
                <div style={{ fontSize: '14px', marginBottom: '8px', opacity: 0.9 }}>Unidades en Stock</div>
                <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{stockValuation.totalUnits.toLocaleString()}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '20px', borderRadius: '8px', backdropFilter: 'blur(10px)' }}>
                <div style={{ fontSize: '14px', marginBottom: '8px', opacity: 0.9 }}>Valor a Precio de Costo (PC)</div>
                <div style={{ fontSize: '28px', fontWeight: 'bold' }}>${stockValuation.totalCostValue.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', padding: '20px', borderRadius: '8px', backdropFilter: 'blur(10px)' }}>
                <div style={{ fontSize: '14px', marginBottom: '8px', opacity: 0.9 }}>Valor a Precio de Venta (PV)</div>
                <div style={{ fontSize: '28px', fontWeight: 'bold' }}>${stockValuation.totalSaleValue.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              <div style={{ background: 'rgba(40, 167, 69, 0.3)', padding: '20px', borderRadius: '8px', backdropFilter: 'blur(10px)', border: '2px solid rgba(40, 167, 69, 0.5)' }}>
                <div style={{ fontSize: '14px', marginBottom: '8px', opacity: 0.9 }}>Ganancia Potencial</div>
                <div style={{ fontSize: '28px', fontWeight: 'bold' }}>${potentialProfit.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>
                  {stockValuation.totalCostValue > 0 ? `+${((potentialProfit / stockValuation.totalCostValue) * 100).toFixed(1)}%` : '0%'}
                </div>
              </div>
            </div>

            {/* Tabla de Distribución por Categoría */}
            <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                    <th style={{ ...thStyle, color: 'white' }}>Categoría</th>
                    <th style={{ ...thStyle, color: 'white', textAlign: 'center' }}>Productos</th>
                    <th style={{ ...thStyle, color: 'white', textAlign: 'center' }}>% del Total</th>
                    <th style={{ ...thStyle, color: 'white', textAlign: 'right' }}>Unidades</th>
                    <th style={{ ...thStyle, color: 'white', textAlign: 'right' }}>Valor PC</th>
                    <th style={{ ...thStyle, color: 'white', textAlign: 'right' }}>Valor PV</th>
                    <th style={{ ...thStyle, color: 'white', textAlign: 'right' }}>Ganancia Pot.</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryDistribution.map((cat, idx) => (
                    <tr key={cat.category} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: idx % 2 === 0 ? '#f8f9fa' : 'white' }}>
                      <td style={{ ...tdStyle, fontWeight: 'bold', color: '#333' }}>{cat.category}</td>
                      <td style={{ ...tdStyle, textAlign: 'center', color: '#666' }}>{cat.count}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            flex: 1,
                            height: '20px',
                            background: '#e9ecef',
                            borderRadius: '10px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${cat.percentage}%`,
                              height: '100%',
                              background: 'linear-gradient(90deg, #667eea, #764ba2)',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                          <span style={{ fontWeight: 'bold', color: '#667eea', minWidth: '50px' }}>
                            {cat.percentage.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#666' }}>{cat.units.toLocaleString()}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#666' }}>
                        ${cat.costValue.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: '#333' }}>
                        ${cat.saleValue.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: '#28a745' }}>
                        ${cat.potentialProfit.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                  {categoryDistribution.length > 0 && (
                    <tr style={{ background: '#667eea', color: 'white', fontWeight: 'bold' }}>
                      <td style={{ ...tdStyle, color: 'white' }}>TOTAL</td>
                      <td style={{ ...tdStyle, textAlign: 'center', color: 'white' }}>
                        {categoryDistribution.reduce((sum, cat) => sum + cat.count, 0)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center', color: 'white' }}>100%</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: 'white' }}>
                        {categoryDistribution.reduce((sum, cat) => sum + cat.units, 0).toLocaleString()}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: 'white' }}>
                        ${categoryDistribution.reduce((sum, cat) => sum + cat.costValue, 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: 'white' }}>
                        ${categoryDistribution.reduce((sum, cat) => sum + cat.saleValue, 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: 'white' }}>
                        ${categoryDistribution.reduce((sum, cat) => sum + cat.potentialProfit, 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Gráfico de Porcentajes por Categoría */}
            <div style={{ marginTop: '20px', background: 'rgba(255,255,255,0.1)', padding: '15px', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>📊 Distribución de Productos por Categoría</h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', height: '200px' }}>
                {categoryDistribution.slice(0, 10).map((cat, idx) => {
                  const maxPercentage = Math.max(...categoryDistribution.map(c => c.percentage), 1);
                  return (
                    <div key={cat.category} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div
                        style={{
                          width: '100%',
                          backgroundColor: `hsl(${(idx * 360) / 10}, 70%, 60%)`,
                          height: `${(cat.percentage / maxPercentage) * 180}px`,
                          minHeight: '5px',
                          borderRadius: '4px 4px 0 0',
                          position: 'relative',
                        }}
                        title={`${cat.category}: ${cat.count} productos (${cat.percentage.toFixed(1)}%)`}
                      >
                        <div style={{ position: 'absolute', top: '-20px', width: '100%', textAlign: 'center', fontSize: '11px', fontWeight: 'bold' }}>
                          {cat.percentage > 0 ? cat.percentage.toFixed(1) + '%' : ''}
                        </div>
                      </div>
                      <div style={{ fontSize: '11px', marginTop: '5px', textAlign: 'center', wordBreak: 'break-word', maxWidth: '100%' }}>
                        {cat.category.length > 10 ? cat.category.substring(0, 10) + '...' : cat.category}
                      </div>
                      <div style={{ fontSize: '10px', opacity: 0.8 }}>{cat.count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Ventas ahora en página separada: /reports/sales */}

          {/* Alertas de stock bajo */}
          {lowStockProducts.length > 0 && (
            <div style={{ ...sectionBox, marginTop: '30px', backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
              <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>⚠️ Productos con Stock Bajo</h2>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ backgroundColor: '#ffe69c' }}>
                    <th style={thStyle}>Producto</th>
                    <th style={thStyle}>Stock Actual</th>
                    <th style={thStyle}>Categoría</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockProducts.map((product) => (
                    <tr key={product._id} style={{ borderBottom: '1px solid #ffc107' }}>
                      <td style={tdStyle}>{product.name}</td>
                      <td style={{ ...tdStyle, fontWeight: 'bold', color: product.stock <= 2 ? '#dc3545' : '#856404' }}>
                        {product.stock}
                      </td>
                      <td style={tdStyle}>{product.category}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <div style={{ marginTop: '50px', fontSize: '10px', color: '#ccc', textAlign: 'right' }}>
        v20251218-0835
      </div>
    </div>
  );
};

// Estilos
const btnNav = {
  background: '#6c757d',
  color: 'white',
  border: 'none',
  padding: '8px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '14px',
};

const btnFilter = {
  background: '#f8f9fa',
  color: '#495057',
  border: '1px solid #dee2e6',
  padding: '8px 16px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '14px',
};

const btnFilterActive = {
  ...btnFilter,
  background: '#007bff',
  color: 'white',
  border: '1px solid #007bff',
};

const btnMini = {
  padding: '4px 8px',
  fontSize: '12px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  background: '#fff',
  cursor: 'pointer',
};

const btnMiniActive = {
  ...btnMini,
  background: '#e9ecef',
  fontWeight: 'bold',
  borderColor: '#999',
};

const card = {
  backgroundColor: 'white',
  padding: '20px',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  textAlign: 'center',
};

const cardIcon = {
  fontSize: '32px',
  marginBottom: '10px',
};

const cardTitle = {
  fontSize: '14px',
  color: '#666',
  marginBottom: '8px',
};

const cardValue = {
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#333',
};

const sectionBox = {
  backgroundColor: 'white',
  padding: '20px',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
};

const thStyle = {
  padding: '12px',
  textAlign: 'left',
  fontWeight: 'bold',
  borderBottom: '2px solid #dee2e6',
};

const tdStyle = {
  padding: '12px',
  textAlign: 'left',
};

// Estilos adicionales usados por _SalesRangeTable
const inputStyle = {
  padding: '8px',
  borderRadius: 4,
  border: '1px solid #ccc',
  marginLeft: 6,
  marginRight: 6,
};

const btnPrimary = {
  background: '#007bff',
  color: '#fff',
  border: 'none',
  padding: '8px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
};

const btnSecondary = {
  background: '#6c757d',
  color: '#fff',
  border: 'none',
  padding: '8px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
};

const tdRight = {
  ...tdStyle,
  textAlign: 'right',
};

// Componente interno para mostrar ventas filtradas por rango de fechas
// Nota: el nombre comienza con _ para indicar que puede permanecer sin usar
// y evitar advertencias de linter sobre variables asignadas pero no usadas.
const _SalesRangeTable = ({ sales = [], from, to, onExport }) => {
  // Similar UX to IVA report: date inputs, Consultar, Exportar, sortable table and totals
  const [fromDate, setFromDate] = useState(from || '');
  const [toDate, setToDate] = useState(to || '');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });

  useEffect(() => {
    // initialize rows filtered by props
    handleQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseDateOnly = (d) => d ? new Date(d) : null;

  const inRange = (sd) => {
    const dt = new Date(sd);
    const f = parseDateOnly(fromDate);
    const t = parseDateOnly(toDate);
    if (f && dt < new Date(f.getFullYear(), f.getMonth(), f.getDate(), 0, 0, 0)) return false;
    if (t && dt > new Date(t.getFullYear(), t.getMonth(), t.getDate(), 23, 59, 59, 999)) return false;
    return true;
  };

  const handleQuery = () => {
    setLoading(true);
    try {
      const filtered = sales.filter(s => {
        const sd = s.createdAt || s.date || s.timestamp;
        if (!sd) return false;
        return inRange(sd);
      });
      setRows(filtered);
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const sortedRows = (() => {
    const r = [...rows];
    const k = sort.key; const dir = sort.dir === 'asc' ? 1 : -1;
    r.sort((a, b) => {
      let va = a[k], vb = b[k];
      if (k === 'createdAt') { va = new Date(a.createdAt || a.date || 0).getTime(); vb = new Date(b.createdAt || b.date || 0).getTime(); }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return r;
  })();

  const totals = rows.reduce((acc, r) => {
    acc.count += 1;
    acc.total += Number(r.total || 0);
    return acc;
  }, { count: 0, total: 0 });

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <label>Desde
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputStyle} />
        </label>
        <label>Hasta
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputStyle} />
        </label>
        <button onClick={handleQuery} disabled={loading} style={btnPrimary}>Consultar</button>
        <button onClick={() => onExport && onExport()} disabled={loading} style={btnSecondary}>Exportar Excel</button>
      </div>

      {loading && <div>Cargando...</div>}

      <div style={{ marginBottom: 8 }}>Resultados: {totals.count} ventas | Total: ${totals.total.toFixed(2)}</div>

      {sortedRows.length === 0 ? (
        <p style={{ color: '#666' }}>No se encontraron ventas en el rango seleccionado.</p>
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ backgroundColor: '#007bff', color: '#fff' }}>
                <th style={thStyle} onClick={() => toggleSort('createdAt')}>Fecha {sort.key === 'createdAt' ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}</th>
                <th style={thStyle}>ID Venta</th>
                <th style={thStyle}>Cliente</th>
                <th style={thStyle}>Ítems</th>
                <th style={thStyle} onClick={() => toggleSort('total')}>Total {sort.key === 'total' ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}</th>
                <th style={thStyle}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((s) => (
                <tr key={s._id || s.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={tdStyle}>{new Date(s.createdAt || s.date).toLocaleString()}</td>
                  <td style={tdStyle}>{s._id || s.id}</td>
                  <td style={tdStyle}>{(s.customer && (s.customer.name || s.customer.fullName)) || s.clientName || s.client || '-'}</td>
                  <td style={tdStyle}>{(s.items && s.items.length) || 0}</td>
                  <td style={tdRight}>${(Number(s.total) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td style={tdStyle}>{s.status || s.estado || 'OK'}</td>
                </tr>
              ))}
              <tr style={{ background: '#fafafa', fontWeight: 'bold' }}>
                <td style={tdStyle} colSpan={4}>Totales</td>
                <td style={tdRight}>${totals.total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td style={tdStyle} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Reports;
